import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';

import { colors } from 'src/global/styles/colors';
import { useLazyGetDepositStatusQuery, useVerifyDepositMutation } from 'src/store/services/project.service';

interface DepositToPublishModalProps {
  open: boolean;
  programId: string;
  onClose: () => void;
  onSuccess: (productionUrl: string) => void;
}

export function DepositToPublishModal({ open, programId, onClose, onSuccess }: DepositToPublishModalProps) {
  const { address } = useAccount();
  const [txHash, setTxHash] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const hasExpiredRef = useRef(false);
  const [getDepositStatus, { data: depositData, isLoading: isLoadingStatus }] = useLazyGetDepositStatusQuery();
  const [verifyDeposit, { isLoading: isVerifying }] = useVerifyDepositMutation();

  // Fetch deposit status when modal opens
  useEffect(() => {
    if (open && programId && address) {
      setTxHash('');
      setTimeLeft(0);
      getDepositStatus({ programId, address });
    }
  }, [open, programId, address, getDepositStatus]);

  useEffect(() => {
    if (!depositData?.close) return;

    const closeTime = new Date(depositData.close);
    closeTime.setMinutes(closeTime.getMinutes() + 10);

    const tick = () => {
      const difference = closeTime.getTime() - Date.now();

      if (difference <= 0) {
        setTimeLeft(0);
        if (!hasExpiredRef.current) {
          hasExpiredRef.current = true;
          toast.info('Deposit window has expired. Please try again.', {
            toastId: 'deposit-window-expired',
            autoClose: 5000,
          });
          onClose();
        }
        return true;
      }

      hasExpiredRef.current = false;
      setTimeLeft(Math.floor(difference / 1000)); // Convert to seconds
      return false;
    };

    if (tick()) return;

    const intervalId = setInterval(() => {
      const expired = tick();
      if (expired) clearInterval(intervalId);
    }, 1000);

    return () => {
      clearInterval(intervalId);
      hasExpiredRef.current = false;
    };
  }, [depositData?.close, onClose]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCopyAddress = useCallback(() => {
    if (depositData?.receiver) {
      navigator.clipboard.writeText(depositData.receiver);
      toast.success('Wallet address copied to clipboard');
    }
  }, [depositData?.receiver]);

  const handleCopyAmount = useCallback(() => {
    if (depositData?.amount) {
      navigator.clipboard.writeText(depositData.amount.toString());
      toast.success('Amount copied to clipboard');
    }
  }, [depositData?.amount]);

  const handleVerify = useCallback(async () => {
    if (!txHash.trim()) {
      toast.error('Please enter a transaction hash');
      return;
    }

    if (!address) {
      toast.error('Wallet not connected');
      return;
    }

    try {
      const result = await verifyDeposit({
        programId,
        txId: txHash,
        address,
      }).unwrap();

      if (result.verified) {
        onSuccess('');
      } else {
        toast.error('Transaction verification failed. Please check your transaction hash.');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'Verification failed. Please try again.');
    }
  }, [txHash, address, programId, verifyDeposit, onSuccess]);

  const truncateAddress = (addr: string) => {
    if (!addr) return '';
    return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
  };

  if (isLoadingStatus) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.pageBg,
            border: `1px solid ${colors.neutral015}`,
            borderRadius: '8px',
            boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.3), 0px 6px 10px 4px rgba(0, 0, 0, 0.15)',
          },
        }}>
        <Box p={4} display='flex' justifyContent='center' alignItems='center'>
          <CircularProgress sx={{ color: colors.brand300 }} />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.pageBg,
          border: `1px solid ${colors.neutral015}`,
          borderRadius: '8px',
          boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.3), 0px 6px 10px 4px rgba(0, 0, 0, 0.15)',
        },
      }}>
      <Stack gap={3} p={3}>
        {/* Header */}
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography
            sx={{
              fontFamily: 'Gotham',
              fontWeight: 500,
              fontSize: '18px',
              lineHeight: '24px',
              color: colors.white,
            }}>
            Deposit to publish
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              width: 24,
              height: 24,
              padding: 0,
              color: colors.white,
            }}>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
              <path
                d='M18 6L6 18M6 6L18 18'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </IconButton>
        </Stack>

        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: colors.white,
          }}>
          To initiate the publish process, please transfer the specified amount of $SFM to the designated wallet:
        </Typography>

        <Stack gap={1.5}>
          <Stack direction='row' alignItems='center' gap={1}>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: '14px',
                color: colors.neutral500,
                flex: 1,
              }}>
              Wallet Address
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '14px',
                color: colors.white,
              }}>
              {depositData?.receiver ? truncateAddress(depositData.receiver) : ''}
            </Typography>
            <IconButton
              onClick={handleCopyAddress}
              sx={{
                width: 20,
                height: 20,
                padding: 0,
                color: colors.white,
              }}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>

          <Stack direction='row' alignItems='center' gap={1}>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: '14px',
                color: colors.neutral500,
                flex: 1,
              }}>
              Required Amount
            </Typography>
            <Stack direction='row' alignItems='center' gap={1}>
              <Box
                component='img'
                src='/icons/common/coins/safemoon.svg'
                alt='SAFEMOON'
                sx={{ width: 18, height: 18 }}
              />
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: colors.white,
                }}>
                {depositData?.amount?.toLocaleString('en-US', { minimumFractionDigits: 1 })} $SFM
              </Typography>
              <IconButton
                onClick={handleCopyAmount}
                sx={{
                  width: 20,
                  height: 20,
                  padding: 0,
                  color: colors.white,
                }}>
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          </Stack>

          <Stack direction='row' alignItems='center' gap={1}>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: '14px',
                color: colors.neutral500,
                flex: 1,
              }}>
              Time Remaining
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '14px',
                color: timeLeft < 120 ? colors.red300 : colors.white,
              }}>
              {formatTime(timeLeft)}
            </Typography>
          </Stack>
        </Stack>

        {/* Divider */}
        <Box sx={{ width: '100%', height: '1px', backgroundColor: colors.neutral015 }} />

        {/* Transaction Instructions */}
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: colors.white,
          }}>
          Once the transfer is complete, enter the{' '}
          <Tooltip
            title={
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: '12px',
                  lineHeight: '16px',
                  padding: '4px',
                }}>
                The transaction hash is a unique identifier for your transfer. After sending the funds to the provided
                address, go to{' '}
                <Link href={'https://solscan.io/'} target={'_blank'}>
                  Solscan
                </Link>{' '}
                and check your transaction history. Find your latest transaction, copy its transaction hash, and paste
                it here.
              </Typography>
            }
            placement='top'
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'rgba(4, 6, 11, 0.9)',
                  borderRadius: '4px',
                  padding: '12px',
                  maxWidth: '300px',
                },
              },
              arrow: {
                sx: {
                  color: 'rgba(4, 6, 11, 0.9)',
                },
              },
            }}>
            <span style={{ textDecoration: 'underline', textDecorationStyle: 'dashed' }}>transaction hash</span>
          </Tooltip>{' '}
          for verification:
        </Typography>

        {/* Transaction Hash Input */}
        <Stack direction='row' alignItems='center' gap={1}>
          <TextField
            fullWidth
            placeholder='Tx Hash'
            value={txHash}
            onChange={e => setTxHash(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Poppins',
                fontSize: '14px',
                color: colors.white,
                backgroundColor: 'transparent',
                borderRadius: '4px',
                '& fieldset': {
                  borderColor: colors.neutral015,
                },
                '&:hover fieldset': {
                  borderColor: colors.neutral60,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.brand300,
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '14px 16px',
                '&::placeholder': {
                  color: colors.neutral500,
                  opacity: 1,
                },
              },
            }}
          />
        </Stack>

        {/* Action Buttons */}
        <Stack direction='row' justifyContent='flex-end' gap={2}>
          <Button
            variant='outlined'
            onClick={onClose}
            disabled={isVerifying}
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '20px',
              color: colors.white,
              borderColor: colors.neutral015,
              backgroundColor: colors.neutral06,
              padding: '10px 32px',
              textTransform: 'none',
              '&:hover': {
                borderColor: colors.neutral60,
                backgroundColor: colors.neutral015,
              },
            }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleVerify}
            disabled={isVerifying || !txHash.trim()}
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '20px',
              color: colors.white,
              backgroundColor: colors.brand300,
              padding: '10px 32px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: colors.brand200,
              },
              '&:disabled': {
                backgroundColor: colors.neutral200,
                color: colors.neutral500,
              },
            }}>
            {isVerifying ? <CircularProgress size={20} sx={{ color: colors.white }} /> : 'Verify'}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
